import React, { useState } from "react";
import CustomModal from "../Common/CustomModal";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { CloseOutlined, TitleSharp, UploadOutlined } from "@mui/icons-material";
import { CustomInput } from "../Common/CustomInput";
import { colors } from "../../theme/theme";
import CustomDatePicker from "../Common/CustomDatePicker";
import { CustomBtn } from "../Common/CustomBtn";
import axiosInstance from "../../utils/config/config";
import { dataURItoBlob } from "../../utils/services/ImageUpload";

const CreatePostModal = ({
  open,
  onClose,
  setLoading,
  setErrorState,
  setSuccess,
  onCreated,
}) => {
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [date, setDate] = useState(null);
  const [images, setImages] = useState([]);

  const handleAddImages = (files) => {
    Array.from(files).forEach((file) => {
      dataURItoBlob(file, (url) =>
        setImages((prev) => [...prev, url])
      );
    });
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePublish = async () => {
    setErrorState({
      error: false,
      errorMsg: "",
    });

    if (!name || !content || !date || images.length === 0) {
      setErrorState({
        error: true,
        errorMsg: "Please fill all the fields",
      });
      return;
    }

    try {
      setLoading(true);

      onClose();
      const response = await axiosInstance.post("/post/createPost", {
        title: name,
        content: content,
        date: date?.format ? date.format("YYYY-MM-DD") : date,
        images: images,
      });

      if (response?.status === 200) {
        setErrorState({
          error: false,
          errorMsg: "",
        });
        setName("");
        setContent("");
        setDate(null);
        setImages([]);
        setSuccess(true);
        onCreated?.();
      }
    } catch (error) {
      setErrorState({
        error: true,
        errorMsg: error?.data?.response?.message || "Failed to create post",
      });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <CustomModal open={open} onClose={onClose} title="Create Post">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "50%",
          margin: "0 auto",
        }}
      >
        <CustomInput
          label={"Post Name"}
          value={name}
          handleChange={(e) => setName(e.target.value)}
          placeholder={"Enter post name"}
          Icon={TitleSharp}
          width={"100%"}
          mT={2}
          borderColor={colors.secondary}
        />
        <CustomInput
          label={"Content"}
          value={content}
          handleChange={(e) => setContent(e.target.value)}
          placeholder={"Enter post content"}
          noIcon
          width={"100%"}
          mT={2}
          borderColor={colors.secondary}
          textArea
        />
        <CustomDatePicker
          label={"Date"}
          value={date}
          setValue={setDate}
          placeholder={"Select date"}
          width={"100%"}
          mT={2}
          borderColor={colors.secondary}
          color={colors.primary}
          iconColor={"rgba(25,34,52,0.5)"}
        />

        <Box sx={{ width: "100%", mt: 2 }}>
          <Typography
            sx={{
              fontFamily: "Mulish-Regular",
              fontSize: "12px",
              color: colors.textColor,
              fontWeight: "700",
              mb: 0.5,
              textTransform: "uppercase",
            }}
          >
            Images
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {images.map((image, index) => (
              <Box
                key={index}
                sx={{
                  position: "relative",
                  width: "90px",
                  height: "90px",
                }}
              >
                <Box
                  component="img"
                  src={image}
                  alt={`upload-${index}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveImage(index)}
                  sx={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    backgroundColor: colors.secondary,
                    "&:hover": { backgroundColor: colors.secondary },
                    p: 0.3,
                  }}
                >
                  <CloseOutlined sx={{ color: "#fff", fontSize: "14px" }} />
                </IconButton>
              </Box>
            ))}
            <Box
              onClick={() => document.getElementById("postImagesInput").click()}
              sx={{
                width: "90px",
                height: "90px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px dashed #ccc",
                borderRadius: "8px",
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f0f0f0" },
              }}
            >
              <input
                type="file"
                id="postImagesInput"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleAddImages(e.target.files)}
              />
              <UploadOutlined sx={{ color: colors.primary }} />
            </Box>
          </Stack>
        </Box>

        <CustomBtn
          text={"Publish"}
          width={"100%"}
          height={"50px"}
          mt={3}
          mb={2}
          onClick={handlePublish}
        />
      </Box>
    </CustomModal>
  );
};

export default CreatePostModal;
